import type { ComponentType } from 'react'

import DiamondMark from '@/components/brand/DiamondMark'
import { OzonLogo, WildberriesLogo, YandexMarketLogo, type BrandLogoProps } from '@/components/brand/marketplace-logos'

type StorefrontCode = 'wildberries' | 'ozon' | 'ymarket' | 'ori'
type StorefrontEntry = Readonly<{ label: string; Logo: ComponentType<BrandLogoProps> }>

function OriLogo({ size = 16, style, className = '' }: BrandLogoProps) {
  return (
    <span style={style} className={`inline-flex text-onyx ${className}`}>
      <DiamondMark size={size} foreground="currentColor" />
    </span>
  )
}

function UnknownLogo({ size = 16, style, className }: BrandLogoProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <circle cx="10" cy="10" r="7" strokeDasharray="2 2.4" />
      <path d="M7.8 8 a2.2 2.2 0 1 1 2.9 2.1 c-.5 .2 -.7 .6 -.7 1.1 v.5" />
      <circle cx="10" cy="14.2" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  )
}

export const storefrontRegistry = Object.freeze({
  wildberries: Object.freeze({ label: 'Wildberries', Logo: WildberriesLogo }),
  ozon: Object.freeze({ label: 'Ozon', Logo: OzonLogo }),
  ymarket: Object.freeze({ label: 'Яндекс Маркет', Logo: YandexMarketLogo }),
  ori: Object.freeze({ label: 'ORI', Logo: OriLogo }),
} satisfies Record<StorefrontCode, StorefrontEntry>)

type StorefrontResolution =
  | { kind: 'known'; providerCode: StorefrontCode; entry: StorefrontEntry }
  | { kind: 'unknown'; providerCode: string }

function isKnownStorefrontCode(providerCode: string): providerCode is StorefrontCode {
  return Object.hasOwn(storefrontRegistry, providerCode)
}

export function resolveStorefront(providerCode: string): StorefrontResolution {
  if (providerCode === 'ori_site') {
    return { kind: 'known', providerCode: 'ori', entry: storefrontRegistry.ori }
  }
  if (isKnownStorefrontCode(providerCode)) {
    return { kind: 'known', providerCode, entry: storefrontRegistry[providerCode] }
  }
  return { kind: 'unknown', providerCode }
}

/** The one way a storefront's logo is drawn anywhere in the product. */
export function StorefrontMark({ providerCode, size = 16, opacity = 1 }: { providerCode: string; size?: number; opacity?: number }) {
  const storefront = resolveStorefront(providerCode)
  const Logo = storefront.kind === 'known' ? storefront.entry.Logo : UnknownLogo
  return (
    <span
      aria-hidden="true"
      data-storefront-mark={storefront.kind === 'known' ? storefront.providerCode : 'unknown'}
      style={{ opacity }}
      className="inline-flex"
    >
      <Logo size={size} />
    </span>
  )
}
